import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { INSPECT_ALL_IMAGES, SHOW_ONLY_SELECTED } from "~/components/CesiumToolBar/constants";
import type { ImageLineMode, ModelSettings, TDegrees } from "~/types/cesium";
import { setAsset } from "./assetSlice";
import { fetchSurvey } from "~/thunks/surveyThunks";
import { fetchAsset } from "~/thunks/assetsThunks";
import type { RootState } from "~/store";


interface BoundingBox {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}
export type BoundingBoxSettings = {
  originalBoundingBox: BoundingBox | null;
  originalCenter: TDegrees | null;
  targetBoundingBox: BoundingBox | null;
  showOriginalBox: boolean;
  showTargetBox: boolean;
}

export interface Translucense {
  show: boolean;
  alpha: number;
  radius: number;
  frontFaceAlpha: number;
}
interface CesiumState {
  modes: string[];
  showImageLocationsLines: boolean;
  inspectionPoint: TDegrees | null;
  cameraDronePosition: any;
  boundingBoxSettings: BoundingBoxSettings | null;
  centerPosition: TDegrees | null;
  modelsCalibration: {
    [key: string]: ModelSettings;
  };
  imageLinesModes: ImageLineMode[];
  showClippingPlanes: boolean;
  showGooglePhotorealistic3DTileset: boolean;
  translucense: Translucense;
  selectedElementsColor: string;
  polygonesTransparency: number;
  useDepthPicking: boolean;
  pickTranslucentDepth: boolean;
  clickMinHeight: number;
  enableCollision: boolean;
}

const initialState: CesiumState = {
  modes: [],
  showImageLocationsLines: false,
  inspectionPoint: null,
  centerPosition: null,
  cameraDronePosition: null,
  boundingBoxSettings: null,
  modelsCalibration: {},
  imageLinesModes: ['rays', 'lines'],
  showClippingPlanes: false,
  showGooglePhotorealistic3DTileset: false,
  translucense: {
    radius: 90,
    show: false,
    alpha: 0.5,
    frontFaceAlpha: 1,
  },
  selectedElementsColor: '#00ff00',
  polygonesTransparency: 0.5,
  useDepthPicking: true,
  pickTranslucentDepth: false,
  enableCollision: false,
  clickMinHeight: -0.5,
};



const cesiumSlice = createSlice({
  name: "cesium",
  initialState,
  reducers: {
    setCesiumMode: (state, action: PayloadAction<string[] | null>) => {
      if (!action.payload) {
        state.modes = [];
      } else {
        if (action.payload.includes(INSPECT_ALL_IMAGES) ||
          state.modes.includes(INSPECT_ALL_IMAGES)
        ) {

          state.inspectionPoint = null;
          state.cameraDronePosition = null;
          // state.showImageLocationsLines = false;
        }
        state.modes = action.payload
        // if (state.modes.includes(action.payload)) {
        //   state.modes = state.modes.filter((mode) => mode !== action.payload);
        // } else {
        //   if (action.payload === SHOW_ONLY_SELECTED) {
        //     state.modes = [...state.modes, action.payload];
        //   } else if (state.modes.includes(SHOW_ONLY_SELECTED)) {
        //     state.modes = [action.payload, SHOW_ONLY_SELECTED];
        //   } else {
        //     state.modes = [action.payload];
        //   }
        // }


      }
    },
    setModelsCalibration: (state, action: PayloadAction<{ [key: string]: ModelSettings }>) => {
      state.modelsCalibration = action.payload;
    },
    setClickMinHeight: (state, action: PayloadAction<number>) => {
      state.clickMinHeight = action.payload;
    },
    setImageLinesModes: (state, action: PayloadAction<ImageLineMode[]>) => {
      state.imageLinesModes = action.payload;
    },
    setInspectionPoint: (state, action: PayloadAction<TDegrees | null>) => {
      state.inspectionPoint = action.payload;
      if (action.payload) {
        state.showImageLocationsLines = true;
        state.cameraDronePosition = null;
      } else {
        state.showImageLocationsLines = false;
      }
    },
    setBoundingBoxSettings: (state, action: PayloadAction<BoundingBoxSettings | null>) => {
      state.boundingBoxSettings = action.payload;
    },
    setCenterPosition: (state, action: PayloadAction<TDegrees | null>) => {
      state.centerPosition = action.payload;

      // Update targetBoundingBox to maintain same dimensions as originalBoundingBox 
      // but centered at the new position
      if (action.payload && state.boundingBoxSettings?.originalBoundingBox) {
        const original = state.boundingBoxSettings.originalBoundingBox;

        // Calculate dimensions from original bounding box
        const halfWidth = (original.maxX - original.minX) / 2;
        const halfHeight = (original.maxY - original.minY) / 2;
        const halfDepth = (original.maxZ - original.minZ) / 2;

        // Create new target bounding box centered at the new position
        state.boundingBoxSettings.targetBoundingBox = {
          minX: action.payload.x - halfWidth,
          maxX: action.payload.x + halfWidth,
          minY: action.payload.y - halfHeight,
          maxY: action.payload.y + halfHeight,
          minZ: action.payload.z - halfDepth,
          maxZ: action.payload.z + halfDepth,
        };
      }
    },
    setShowClippingPlanes: (state, action: PayloadAction<boolean>) => {
      state.showClippingPlanes = action.payload;
    },
    setShowGooglePhotorealistic3DTileset: (state, action: PayloadAction<boolean>) => {
      state.showGooglePhotorealistic3DTileset = action.payload;
    },
    setTranslucense: (state, action: PayloadAction<Translucense>) => {
      state.translucense = action.payload;
    },
    setSelectedElementsColor: (state, action: PayloadAction<string>) => {
      state.selectedElementsColor = action.payload;
    },
    setPolygonesTransparency: (state, action: PayloadAction<number>) => {
      state.polygonesTransparency = action.payload;
    },
    setPickTranslucentDepth: (state, action: PayloadAction<boolean>) => {
      state.pickTranslucentDepth = action.payload;
    },
    setEnableCollision: (state, action: PayloadAction<boolean>) => {
      state.enableCollision = action.payload;
    }

  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSurvey.pending, (state, action) => {
        state.inspectionPoint = null;
        state.cameraDronePosition = null;
        state.showImageLocationsLines = false;
        state.imageLinesModes = ['rays', 'lines'];
        state.centerPosition = null;
      })
      // modelsCalibration is now set via dispatch in the thunk
      .addCase(fetchAsset.fulfilled, (state, action: PayloadAction<any>) => {
        const { model, asset } = action.payload;
        const modelsCalibration: { [key: string]: ModelSettings } = {};
        // state.centerPosition = Cartesian3.fromDegrees(
        //   asset.lon || 0,
        //   asset.lat || 0,
        //   0
        // ) as unknown as TDegrees;

        if (!model) return;
        const modelKey = String(model.id);
        if (model.calibration_data) {
          const calibrationData: ModelSettings = JSON.parse(model.calibration_data);
          // console.log(model.name, calibrationData);
          modelsCalibration[modelKey] = {
            modelId: model.id,
            modelName: model.name,
            disabled: false,
            show: calibrationData.show ?? true,
            alpha: calibrationData.alpha || 1,
            mainColor: calibrationData.mainColor,
            pointSize: 1,
            calibrationData: {
              lon: calibrationData.calibrationData.lon,
              lat: calibrationData.calibrationData.lat,
              height: calibrationData.calibrationData.height,
              "rotate-x": calibrationData.calibrationData["rotate-x"],
              "rotate-y": calibrationData.calibrationData["rotate-y"],
              "rotate-z": calibrationData.calibrationData["rotate-z"],
            },
          };
        } 
        else {
          modelsCalibration[modelKey] = {
            modelId: model.id ?? model.bid,
            modelName: model.name,
            disabled: false,
            show: true,
            alpha: 1,
            mainColor: null,
            pointSize: 1,
            calibrationData: {
              lon: asset.lon || 0,
              lat: asset.lat || 0,
              height: 0,
              "rotate-x": 0,
              "rotate-y": 0,
              "rotate-z": 0,
            },
          };
        }
        state.modelsCalibration = {
          ...state.modelsCalibration,
          ...modelsCalibration,
        };
      })
      .addCase(setAsset, (state, action: PayloadAction<any>) => {
        Object.assign(state, initialState);
      })
  },
});

export default cesiumSlice.reducer;
export const {
  setInspectionPoint,
  setCesiumMode,
  setModelsCalibration,
  setImageLinesModes,
  setCenterPosition,
  setBoundingBoxSettings,
  setShowClippingPlanes,
  setShowGooglePhotorealistic3DTileset,
  setTranslucense,
  setSelectedElementsColor,
  setPolygonesTransparency,
  setPickTranslucentDepth,
  setEnableCollision,
  setClickMinHeight,
} = cesiumSlice.actions;

export const selectCesiumModes = (state: RootState) => state.cesium.modes;
export const selectCurrentMode = (state: RootState) => state.cesium.modes.length > 0 ? state.cesium.modes[0] : null;
export const modelsCalibration = (state: RootState) => state.cesium.modelsCalibration;
export const selectImageLinesModes = (state: RootState) => state.cesium.imageLinesModes;
export const selectInspectionPoint = (state: RootState) => state.cesium.inspectionPoint;
export const selectBoundingBoxSettings = (state: RootState) => state.cesium.boundingBoxSettings;
export const selectCenterPosition = (state: RootState) => state.cesium.centerPosition;
export const selectShowImageLocationsLines = (state: RootState) => state.cesium.showImageLocationsLines;
export const selectShowClippingPlanes = (state: RootState) => state.cesium.showClippingPlanes;
export const selectShowGooglePhotorealistic3DTileset = (state: RootState) => state.cesium.showGooglePhotorealistic3DTileset;
export const selectTranslucense = (state: RootState) => state.cesium.translucense;
export const selectSelectedElementsColor = (state: RootState) => state.cesium.selectedElementsColor;
export const selectPolygonesTransparency = (state: RootState) => state.cesium.polygonesTransparency;
export const selectUseDepthPicking = (state: RootState) => state.cesium.useDepthPicking;
export const selectPickTranslucentDepth = (state: RootState) => state.cesium.pickTranslucentDepth;
export const selectEnableCollision = (state: RootState) => state.cesium.enableCollision;
export const selectClickMinHeight = (state: RootState) => state.cesium.clickMinHeight;
// Export the BoundingBox type
export type { BoundingBox };
